import { combineRefs } from '@visiba/react-std';
import { ForwardedRef, forwardRef } from 'react';
import { Controller, RegisterOptions } from 'react-hook-form';
import { useText } from '../../../utils/hooks/use_text';
import { useFormContext } from '../form';
import { InputPropsHelper } from '../form/form.helpers';
import { CellulaInputTextAreaRoot, InputTextAreaRootProps } from './_input_text_area_root.component';

type InputTextAreaProps = {
	options?: RegisterOptions;
} & InputTextAreaRootProps;

function CellulaInputTextArea(props: InputTextAreaProps, ref: ForwardedRef<HTMLTextAreaElement>): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const formContext = useFormContext();
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const staticTexts = useText();

	if (formContext == null || props.name == null) {
		return <CellulaInputTextAreaRoot {...props} ref={ref} />;
	}

	return (
		<Controller
			control={formContext.control.control}
			name={props.name}
			defaultValue={props.defaultValue}
			rules={{
				...props.options,
				onChange: props.onChange,
				onBlur: props.onBlur,
			}}
			disabled={props.disabled}
			render={({ field, fieldState, formState }) => {
				const inputPropsHelper = new InputPropsHelper(props, staticTexts, fieldState, formState);

				return (
					<CellulaInputTextAreaRoot
						{...props}
						{...field}
						optional={inputPropsHelper.isOptional()}
						required={inputPropsHelper.isRequired()}
						invalid={inputPropsHelper.isInvalid()}
						invalidText={inputPropsHelper.invalidText()}
						readOnly={inputPropsHelper.isReadOnly()}
						value={field.value ?? ''}
						defaultValue={undefined}
						ref={combineRefs([ref, field.ref])}
					/>
				);
			}}
		/>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to provide a complete set of elements to let the user input large
 * amount of content. Including label, input field, error handling, helper text,
 * etc.
 */
const InputTextArea = forwardRef(CellulaInputTextArea);

export type { InputTextAreaProps };
export { InputTextArea };
